import * as Sentry from '@sentry/react';
import { VERSION, COMMIT_SHA } from '@/version';

/**
 * Inicializa Sentry para captura de erros JS em produção.
 *
 * Projeto: react-spa no org igm-99
 * Triagem diária: cron OS 8h BRT na VPS (aproveita pipeline do app antigo)
 *
 * Chamado uma vez em main.tsx antes de renderizar o App.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;

  // Em desenvolvimento, não inicializa (evita ruído + economiza quota)
  if (!dsn || import.meta.env.DEV) {
    console.info('[Sentry] desabilitado em desenvolvimento');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release: `react-spa@${VERSION}+${COMMIT_SHA}`,

    // Performance monitoring (10% das transações em prod)
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Replay de sessões em caso de erro (5% normal, 100% em erros)
    replaysSessionSampleRate: 0.0, // desligado por padrão pra não consumir quota
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay só ativa se quisermos usar
      // Sentry.replayIntegration(),
    ],

    // Filtros de ruído conhecido
    ignoreErrors: [
      // Erros de extensões do navegador
      'ResizeObserver loop',
      'Non-Error promise rejection captured',
      // Network errors fora do nosso controle
      'NetworkError',
      'Failed to fetch',
    ],

    beforeSend(event) {
      // Adiciona contexto útil
      if (event.user) {
        delete event.user.ip_address; // privacidade
      }
      return event;
    },
  });

  console.info(`[Sentry] inicializado · env=${environment} · release=${VERSION}+${COMMIT_SHA}`);
}

/**
 * Marca o usuário logado pra rastreamento de erros.
 * Chamado quando o user faz login.
 */
export function setSentryUser(user: { id: string; email?: string; departments?: string[] }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
  if (user.departments) {
    Sentry.setTag('departments', user.departments.join(','));
  }
}

/** Limpa contexto de usuário no logout. */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Reporta erro manual pra Sentry com contexto adicional.
 * Útil em catch blocks de operações importantes.
 */
export function reportError(error: unknown, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}
